import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import type {
  GuidedChatCreate,
  GuidedChatRead,
  GuidedChatListResponse,
  MessageSchema,
} from "../../__generated__/api/wrenAPI.schemas";
import {
  getGuidedChatWebGuidedChatsGuidedChatIdGet,
  listGuidedChatsWebGuidedChatsGet,
  createGuidedChatWebGuidedChatsPost,
  completeGuidedChatWebGuidedChatsGuidedChatIdCompletePost,
  updateGuidedChatWebGuidedChatsGuidedChatIdPatch,
} from "../../__generated__/api/web/web";

// Re-export types for convenience
export type { GuidedChatRead, MessageSchema };
export type GuidedChatUpdateFeedbackRating = 'no' | 'a_little' | 'yes' | null;

export function useGuidedChat(
  guidedChatId: number | null,
  options?: { pollForInviteMessage?: boolean }
) {
  return useQuery<GuidedChatRead>({
    queryKey: ["guided-chat", guidedChatId],
    queryFn: async () => {
      const response = await getGuidedChatWebGuidedChatsGuidedChatIdGet(guidedChatId!);
      return response.data as GuidedChatRead;
    },
    enabled: guidedChatId !== null,
    // Poll every 2s while waiting for invite_message to be generated
    refetchInterval: options?.pollForInviteMessage
      ? (query) => (query.state.data?.invite_message ? false : 2000)
      : false,
  });
}

export function useGuidedChatList() {
  return useQuery<GuidedChatListResponse>({
    queryKey: ["guided-chats"],
    queryFn: async () => {
      const response = await listGuidedChatsWebGuidedChatsGet();
      return response.data as GuidedChatListResponse;
    },
  });
}

export function useCreateGuidedChat() {
  const queryClient = useQueryClient();

  return useMutation<GuidedChatRead, Error, { chat_type: string }>({
    mutationFn: async (data) => {
      console.log("[useCreateGuidedChat] Starting POST /web/guided-chats", {
        data,
      });
      try {
        const response = await createGuidedChatWebGuidedChatsPost(data as GuidedChatCreate);
        const responseData = response.data as GuidedChatRead;
        console.log("[useCreateGuidedChat] Response received:", {
          status: response.status,
          data: responseData,
        });
        return responseData;
      } catch (error) {
        console.error("[useCreateGuidedChat] Request failed:", error);
        throw error;
      }
    },
    onSuccess: (data) => {
      console.log("[useCreateGuidedChat] onSuccess called:", data);
      queryClient.invalidateQueries({ queryKey: ["guided-chats"] });
    },
    onError: (error) => {
      console.error("[useCreateGuidedChat] onError called:", error);
    },
  });
}

export function useCompleteGuidedChat() {
  const queryClient = useQueryClient();

  return useMutation<GuidedChatRead, Error, number>({
    mutationFn: async (guidedChatId) => {
      const response = await completeGuidedChatWebGuidedChatsGuidedChatIdCompletePost(guidedChatId);
      return response.data as GuidedChatRead;
    },
    onSuccess: (_, guidedChatId) => {
      queryClient.invalidateQueries({ queryKey: ["guided-chat", guidedChatId] });
      queryClient.invalidateQueries({ queryKey: ["guided-chats"] });
    },
  });
}

export function useUpdateGuidedChat() {
  const queryClient = useQueryClient();

  return useMutation<
    GuidedChatRead,
    Error,
    { guidedChatId: number; feedbackRating: NonNullable<GuidedChatUpdateFeedbackRating> }
  >({
    mutationFn: async ({ guidedChatId, feedbackRating }) => {
      const response = await updateGuidedChatWebGuidedChatsGuidedChatIdPatch(guidedChatId, {
        feedback_rating: feedbackRating,
      });
      // customInstance throws on non-2xx, so response is always the success variant
      return response.data as GuidedChatRead;
    },
    onSuccess: (_, { guidedChatId }) => {
      queryClient.invalidateQueries({ queryKey: ["guided-chat", guidedChatId] });
    },
  });
}
